import { AdminAuthBusyError } from '@/utils/apiClient'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useClipboard,
  useColorModeValue,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiCopy, FiKey, FiRefreshCw, FiShield } from 'react-icons/fi'
import { useSearchParams } from 'react-router-dom'
import { AdminPasswordPanel } from '@/components/account/AdminPasswordPanel'
import { AdminMfaSetupPanel } from '@/components/account/AdminMfaSetupPanel'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/useToast'
import { authService } from '@/services/authService'
import type { AdminMfaSetupResponse, AdminMfaStatus } from '@/types'
import { getApiClientHeader } from '@/utils/apiConfig'
import { formatDate } from '@/utils/formatUtils'
import { ACCOUNT_TABS, getAccountTabIndex, resolveAccountTab } from './accountTabs'

type SensitiveMfaAction = 'reset-mfa' | 'regenerate-backup'
type MfaStatusRequest = {
  clientHeader: string
  promise: Promise<AdminMfaStatus>
}

let mfaStatusRequest: MfaStatusRequest | null = null
let lastMfaStatusFailureToastAt = 0

const getDedupedMfaStatus = (): Promise<AdminMfaStatus> => {
  const clientHeader = getApiClientHeader()
  if (!mfaStatusRequest || mfaStatusRequest.clientHeader !== clientHeader) {
    const promise = authService.getMfaStatus().finally(() => {
      if (mfaStatusRequest?.promise === promise) {
        mfaStatusRequest = null
      }
    })
    mfaStatusRequest = { clientHeader, promise }
  }
  return mfaStatusRequest.promise
}

const shouldShowMfaStatusFailureToast = (): boolean => {
  const now = Date.now()
  if (now - lastMfaStatusFailureToastAt < 3000) {
    return false
  }
  lastMfaStatusFailureToastAt = now
  return true
}

export function AccountPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = resolveAccountTab(searchParams.get('tab'))
  const tabIndex = getAccountTabIndex(activeTab)
  const { error: showToastError, success: showToastSuccess } = useToast()
  const { completeLogin } = useAuth()
  const [status, setStatus] = useState<AdminMfaStatus | null>(null)
  const [isLoadingStatus, setIsLoadingStatus] = useState(true)
  const [isWorking, setIsWorking] = useState(false)
  const [totp, setTotp] = useState('')
  const [sensitiveAction, setSensitiveAction] = useState<SensitiveMfaAction | null>(null)
  const [sensitiveTotp, setSensitiveTotp] = useState('')
  const [setup, setSetup] = useState<AdminMfaSetupResponse | null>(null)
  const [backupCode, setBackupCode] = useState('')
  const backupClipboard = useClipboard(backupCode)
  const mutedColor = useColorModeValue('gray.600', 'gray.300')

  const loadStatus = useCallback(async () => {
    setIsLoadingStatus(true)
    try {
      setStatus(await getDedupedMfaStatus())
    } catch {
      if (shouldShowMfaStatusFailureToast()) {
        showToastError({
          id: 'account-security-mfa-status-load-failed',
          titleKey: 'account.security.statusLoadFailed',
          descriptionKey: 'account.security.statusLoadDescription',
        })
      }
    } finally {
      setIsLoadingStatus(false)
    }
  }, [showToastError])

  useEffect(() => {
    if (activeTab === 'mfa') {
      void loadStatus()
    }
  }, [activeTab, loadStatus])

  useEffect(() => {
    if (searchParams.get('tab') === activeTab) {
      return
    }
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('tab', activeTab)
    setSearchParams(nextParams, { replace: true })
  }, [activeTab, searchParams, setSearchParams])

  const handleTabChange = (index: number) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('tab', ACCOUNT_TABS[index] ?? 'mfa')
    setSearchParams(nextParams)
  }

  const hasRecentMfaCheck = useMemo(() => {
    if (!status?.lastVerifiedAt) {
      return false
    }
    const verifiedAt = Date.parse(status.lastVerifiedAt)
    return Number.isFinite(verifiedAt) && Date.now() - verifiedAt < 10 * 60 * 1000
  }, [status?.lastVerifiedAt])

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!/^\d{6}$/.test(totp)) {
      return
    }
    setIsWorking(true)
    try {
      const result = await authService.verifyMfa(totp)
      setStatus((current) =>
        current ? { ...current, lastVerifiedAt: result.verifiedAt } : current
      )
      setTotp('')
      showToastSuccess({ titleKey: 'account.security.verifySuccess' })
    } catch {
      showToastError({
        titleKey: 'account.security.verifyFailed',
        descriptionKey: 'auth.mfa.invalidCode',
      })
    } finally {
      setIsWorking(false)
    }
  }

  const startMfaSetup = async () => {
    setIsWorking(true)
    try {
      const nextSetup = await authService.setupMfa()
      setSetup(nextSetup)
      setBackupCode('')
    } catch {
      showToastError({
        titleKey: 'account.security.setupStartFailed',
        descriptionKey: 'account.security.setupStartDescription',
      })
    } finally {
      setIsWorking(false)
    }
  }

  const openSensitiveMfaDialog = (action: SensitiveMfaAction) => {
    setSensitiveAction(action)
    setSensitiveTotp('')
  }

  const closeSensitiveMfaDialog = () => {
    if (isWorking) {
      return
    }
    setSensitiveAction(null)
    setSensitiveTotp('')
  }

  const handleStartSetup = async () => {
    if (status?.isEnabled) {
      openSensitiveMfaDialog('reset-mfa')
      return
    }

    await startMfaSetup()
  }

  const handleConfirmSetup = async (code: string) => {
    if (!setup) {
      return
    }
    setIsWorking(true)
    try {
      const result = await authService.confirmMfa({
        pendingToken: setup.pendingToken,
        totp: code,
      })
      completeLogin(result.login)
      setBackupCode(result.backupCode)
      setSetup(null)
      setStatus({
        isEnabled: true,
        isConsistent: true,
        lastVerifiedAt: null,
      })
      showToastSuccess({ titleKey: 'account.security.setupSuccess' })
    } catch (error) {
      showToastError({
        titleKey: 'auth.mfa.setupFailed',
        descriptionKey: error instanceof AdminAuthBusyError
          ? 'auth.previousSessionPending' : 'auth.mfa.invalidCode',
      })
    } finally {
      setIsWorking(false)
    }
  }

  const handleRegenerateBackup = () => {
    openSensitiveMfaDialog('regenerate-backup')
  }

  const handleSensitiveMfaSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!sensitiveAction || !/^\d{6}$/.test(sensitiveTotp)) {
      return
    }

    setIsWorking(true)
    try {
      let verifiedAt: string
      try {
        const verification = await authService.verifyMfa(sensitiveTotp)
        verifiedAt = verification.verifiedAt
      } catch {
        showToastError({
          titleKey: 'account.security.verifyFailed',
          descriptionKey: 'auth.mfa.invalidCode',
        })
        return
      }

      if (sensitiveAction === 'reset-mfa') {
        try {
          const nextSetup = await authService.resetMfa()
          setSetup(nextSetup)
          setBackupCode('')
        } catch {
          showToastError({
            titleKey: 'account.security.setupStartFailed',
            descriptionKey: 'account.security.resetStartDescription',
          })
          return
        }
      } else {
        try {
          const result = await authService.regenerateBackupCode()
          setBackupCode(result.backupCode)
          showToastSuccess({ titleKey: 'account.security.backupRegenerated' })
        } catch {
          showToastError({
            titleKey: 'account.security.backupRegenerateFailed',
            descriptionKey: 'account.security.backupRegenerateDescription',
          })
          return
        }
      }

      setStatus((current) => (current ? { ...current, lastVerifiedAt: verifiedAt } : current))
      setSensitiveAction(null)
      setSensitiveTotp('')
    } finally {
      setIsWorking(false)
    }
  }

  const renderBackupCode = () => {
    if (!backupCode) {
      return null
    }
    return (
      <Alert status="warning" alignItems="flex-start">
        <AlertIcon />
        <Stack flex="1" spacing="3">
          <AlertDescription fontSize="xs">
            {t('auth.mfa.backupCodeWarning')}
          </AlertDescription>
          <HStack>
            <Code flex="1" px="3" py="2" fontSize="sm" overflowWrap="anywhere">
              {backupCode}
            </Code>
            <Tooltip
              label={backupClipboard.hasCopied ? t('common.copySuccess') : t('common.copy')}
            >
              <IconButton
                aria-label={t('auth.mfa.copyBackupCode')}
                icon={<FiCopy />}
                onClick={backupClipboard.onCopy}
                variant="outline"
              />
            </Tooltip>
          </HStack>
          <Button
            alignSelf="flex-start"
            size="sm"
            variant="outline"
            onClick={() => setBackupCode('')}
          >
            {t('account.security.dismissBackupCode')}
          </Button>
        </Stack>
      </Alert>
    )
  }

  const sensitiveActionDescriptionKey =
    sensitiveAction === 'reset-mfa'
      ? 'account.security.actionMfaResetDescription'
      : 'account.security.actionMfaBackupDescription'
  const sensitiveActionLabelKey =
    sensitiveAction === 'reset-mfa'
      ? 'account.security.resetAction'
      : 'account.security.backupAction'

  return (
    <Box maxW="900px">
      <Stack spacing="6">
        <Box>
          <Heading size="lg">{t('account.title')}</Heading>
          <Text color={mutedColor} mt="1">
            {t('account.description')}
          </Text>
        </Box>

        <Tabs index={tabIndex} onChange={handleTabChange} colorScheme="admin" isLazy>
          <TabList>
            <Tab>
              <HStack spacing="2">
                <FiShield />
                <Text>{t('account.security.tab')}</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack spacing="2">
                <FiKey />
                <Text>{t('account.password.tab')}</Text>
              </HStack>
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px="0" py="6">
              <Stack spacing="6">
                {isLoadingStatus ? (
                  <HStack>
                    <Spinner size="sm" />
                    <Text color={mutedColor}>{t('common.loading')}</Text>
                  </HStack>
                ) : (
                  <>
                    <HStack justify="space-between" align="start" flexWrap="wrap" gap="3">
                      <Box>
                        <HStack>
                          <Heading size="sm">{t('account.security.mfaTitle')}</Heading>
                          <Badge colorScheme={status?.isEnabled ? 'green' : 'orange'}>
                            {status?.isEnabled
                              ? t('account.security.enabled')
                              : t('account.security.disabled')}
                          </Badge>
                        </HStack>
                        <Text color={mutedColor} fontSize="sm" mt="1">
                          {t('account.security.mfaDescription')}
                        </Text>
                      </Box>
                      <RefreshIconButton
                        label={t('common.refresh')}
                        onClick={() => void loadStatus()}
                      />
                    </HStack>

                    {!status?.isConsistent && (
                      <Alert status="error">
                        <AlertIcon />
                        <AlertDescription>
                          {t('account.security.inconsistent')}
                        </AlertDescription>
                      </Alert>
                    )}

                    {renderBackupCode()}

                    {setup ? (
                      <AdminMfaSetupPanel
                        setup={setup}
                        isLoading={isWorking}
                        onConfirm={handleConfirmSetup}
                        onCancel={() => setSetup(null)}
                      />
                    ) : (
                      <>
                        {status?.isEnabled && (
                          <>
                            <Divider />
                            <Box>
                              <Heading size="sm" mb="1">
                                {t('account.security.stepUpTitle')}
                              </Heading>
                              <Text color={mutedColor} fontSize="sm" mb="4">
                                {t('account.security.stepUpDescription')}
                              </Text>
                              <form onSubmit={handleVerify}>
                                <HStack align="end" maxW="480px">
                                  <FormControl>
                                    <FormLabel>{t('auth.mfa.code')}</FormLabel>
                                    <Input
                                      value={totp}
                                      onChange={(event) =>
                                        setTotp(
                                          event.target.value.replace(/\D/g, '').slice(0, 6)
                                        )
                                      }
                                      inputMode="numeric"
                                      autoComplete="one-time-code"
                                      maxLength={6}
                                      placeholder={t('auth.mfa.codePlaceholder')}
                                    />
                                    <FormHelperText minH="5">
                                      {hasRecentMfaCheck
                                        ? t('account.security.stepUpActive', {
                                            time: status.lastVerifiedAt
                                              ? formatDate(status.lastVerifiedAt)
                                              : '',
                                          })
                                        : t('account.security.stepUpInactive')}
                                    </FormHelperText>
                                  </FormControl>
                                  <Button
                                    type="submit"
                                    colorScheme="admin"
                                    leftIcon={<FiShield />}
                                    isLoading={isWorking}
                                    isDisabled={!/^\d{6}$/.test(totp)}
                                    mb="7"
                                  >
                                    {t('account.security.verify')}
                                  </Button>
                                </HStack>
                              </form>
                            </Box>
                          </>
                        )}

                        <Divider />
                        <HStack align="start" justify="space-between" flexWrap="wrap" gap="4">
                          <Box>
                            <Heading size="sm">
                              {status?.isEnabled
                                ? t('account.security.resetTitle')
                                : t('account.security.enableTitle')}
                            </Heading>
                            <Text color={mutedColor} fontSize="sm" mt="1">
                              {status?.isEnabled
                                ? t('account.security.resetDescription')
                                : t('account.security.enableDescription')}
                            </Text>
                          </Box>
                          <Button
                            leftIcon={<FiRefreshCw />}
                            colorScheme={status?.isEnabled ? 'orange' : 'admin'}
                            variant={status?.isEnabled ? 'outline' : 'solid'}
                            onClick={handleStartSetup}
                            isLoading={isWorking}
                          >
                            {status?.isEnabled
                              ? t('account.security.resetAction')
                              : t('account.security.enableAction')}
                          </Button>
                        </HStack>

                        {status?.isEnabled && (
                          <>
                            <Divider />
                            <HStack
                              align="start"
                              justify="space-between"
                              flexWrap="wrap"
                              gap="4"
                            >
                              <Box>
                                <Heading size="sm">
                                  {t('account.security.backupTitle')}
                                </Heading>
                                <Text color={mutedColor} fontSize="sm" mt="1">
                                  {t('account.security.backupDescription')}
                                </Text>
                              </Box>
                              <Button
                                leftIcon={<FiKey />}
                                variant="outline"
                                onClick={handleRegenerateBackup}
                                isLoading={isWorking}
                              >
                                {t('account.security.backupAction')}
                              </Button>
                            </HStack>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </Stack>
            </TabPanel>
            <TabPanel px="0" py="6">
              <AdminPasswordPanel />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Stack>

      <Modal isOpen={Boolean(sensitiveAction)} onClose={closeSensitiveMfaDialog} isCentered>
        <ModalOverlay />
        <ModalContent mx="4">
          <form onSubmit={handleSensitiveMfaSubmit}>
            <ModalHeader>{t('account.security.stepUpTitle')}</ModalHeader>
            <ModalCloseButton isDisabled={isWorking} />
            <ModalBody>
              <Stack spacing="4">
                <Text color={mutedColor} fontSize="sm">
                  {t(sensitiveActionDescriptionKey)}
                </Text>
                <FormControl isRequired>
                  <FormLabel>{t('auth.mfa.code')}</FormLabel>
                  <Input
                    value={sensitiveTotp}
                    onChange={(event) =>
                      setSensitiveTotp(event.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder={t('auth.mfa.codePlaceholder')}
                    autoFocus
                  />
                </FormControl>
              </Stack>
            </ModalBody>
            <ModalFooter gap="3">
              <Button variant="ghost" onClick={closeSensitiveMfaDialog} isDisabled={isWorking}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                colorScheme={sensitiveAction === 'reset-mfa' ? 'orange' : 'admin'}
                isLoading={isWorking}
                isDisabled={!/^\d{6}$/.test(sensitiveTotp)}
              >
                {t(sensitiveActionLabelKey)}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  )
}
